import * as _ from "underscore";

export const permissions = {
  free: {
    phrases: 10,
    definitions: 10,
    reviewSummaries: 1,
    chat: 0,
    editText: 0,
    contextTranslations: 10,
    guideWords: 0,
  },
  standard: {
    phrases: 4500,
    definitions: 2500,
    reviewSummaries: 200,
    chat: 2,
    editText: 5,
    contextTranslations: 4500,
    guideWords: 500000,
  },
  plus: {
    phrases: 9000,
    definitions: 5000,
    reviewSummaries: 400,
    chat: 200,
    editText: 20,
    contextTranslations: 9000,
    guideWords: 1500000,
  },
};

// `guideWords` is the monthly (calendar month, per user) cap on words of book
// text processed by the AI Reading Guide feature. Unlike the other permissions
// above (daily counts resetting at the start of the user's day, enforced via
// src/dal/Usage.js), this is enforced monthly via src/dal/GuideUsage.js because
// each generation has a direct per-word AI cost.
//
// Cost basis (gpt-5.4-mini, July 2026: $0.75/M input tokens, $4.50/M output):
// input runs ~1.5 tokens per word position typically, ~2.5 worst case for
// highly inflected or character-counted languages; books of 20k+ words run at
// medium reasoning effort (~10k fixed + ~0.13/word reasoning tokens, measured)
// and charge a flat 25k-word surcharge to cover the fixed part. That works out
// at ~$2.00 per million charged words typical, ~$2.50 worst case (see
// docs/guide-feature-costs.md for the full working and measurements).
// The caps keep worst-case monthly guide cost well within 25% of each plan's
// price:
//   Premium      $6/mo: 500K words → ≤ ~$1.25 (21%), typically ~$1.00 (17%)
//   Premium Plus $15/mo: 1.5M words → ≤ ~$3.75 (25%), typically ~$3.00 (20%)
// In practice 500K charged words ≈ 4-5 full-length novels a month, so the caps
// exist as a cost safety valve rather than something users should notice.

// Relative entitlement value of the two premium tiers
// (docs/todos/tasks/stripeRefactor.md). This is an explicit entitlement
// policy, not an amount inferred from a Stripe charge: it drives EVERY plan
// change — an AccessGrant's `endsAt`, a bridge's `trial_end`, and an active
// paid Subscription's converted billing date — so the 2.5x lives in exactly
// one place. See src/accessValue.js for the conversions themselves.
//
// The 2.5 IS the current price ratio, in both cadences:
//   Premium      $6 / month, $48 / year   (premiumMonthly6, premiumYearly9)
//   Premium Plus $15 / month, $120 / year (premiumPlusMonthly1,
//                                          premiumPlusYearly1)
// so converting time at 2.5x preserves exactly the value a monetary proration
// would. It is a pinned constant, not a derived one: if either tier's price
// ever moves independently, every conversion is silently wrong until this is
// updated. src/test/accessValue.test.js asserts the ratio still matches the
// configured Prices, so that change can't ship unnoticed.
export const accessValueWeights = {
  standard: 1,
  plus: 2.5,
};

export const plans = {
  // -- Current plans --
  free: {
    permissions: "free",
    months: 0,
    price: 0,
    default: true,
  },

  premiumPlusMonthly1: {
    permissions: "plus",
    months: 1,
    price: 15.0,
    default: true,
  },
  premiumPlusYearly1: {
    permissions: "plus",
    months: 12,
    price: 120.0,
    default: true,
  },

  premiumMonthly6: {
    permissions: "standard",
    months: 1,
    price: 6.0,
    default: true,
  },
  premiumYearly9: {
    permissions: "standard",
    months: 12,
    price: 48.0,
    default: true,
  },

  // -- Deprecated / non-default plans --
  premiumYearly10: {
    permissions: "standard",
    months: 12,
    price: 60.0,
  },
  premiumMonthly4: {
    permissions: "standard",
    months: 1,
    price: 4.0,
  },
  premiumMonthly5: {
    permissions: "standard",
    months: 1,
    price: 5.0,
  },
  premiumYearly8: {
    permissions: "standard",
    months: 12,
    price: 30.0,
  },
  premiumYearly7: {
    permissions: "standard",
    months: 12,
    price: 48.0,
  },
  // premiumYearly6: {
  //   permissions: "standard",
  //   months: 12,
  //   price: 36.0,
  // },
  premiumMonthly3: {
    permissions: "standard",
    months: 1,
    price: 3.0,
  },
  premiumYearly4: {
    permissions: "standard",
    months: 12,
    price: 24.0,
  },
  // premiumYearly5: {
  //   permissions: "standard",
  //   months: 12,
  //   price: 36.0,
  // },
  premiumLife2: {
    permissions: "standard",
    months: 1200,
    price: 150,
  },
  premiumYearly3: {
    permissions: "standard",
    months: 12,
    price: 29.99,
  },
  premiumLife: {
    permissions: "standard",
    months: 1200,
    price: 99.99,
  },
  premiumMonthly: {
    permissions: "standard",
    months: 3,
    price: 4.99,
  },
  premiumYearly2: {
    permissions: "standard",
    months: 12,
    price: 14.99,
  },
  standard: {
    permissions: "standard",
    // various months and price!
  },
  plus: {
    permissions: "plus",
    // various months and price!
  },
  freeForever: {
    permissions: "plus",
    months: 0,
    price: 0,
  },
  unsubscribed: {
    permissions: "free",
    months: 0,
    price: 0,
  },
  yearly1000: {
    permissions: "standard",
    months: 12,
    price: 10.0,
  },
  yearly999: {
    permissions: "standard",
    months: 12,
    price: 9.99,
  },
  premiumMonthly2: {
    permissions: "standard",
    months: 3,
    price: 9.99,
  },
};

const defaultPlans = {};
for (const [name, plan] of _.pairs(plans)) {
  if (plan.default) {
    defaultPlans[`${plan.permissions}-${plan.months}`] = {
      name,
      ...plan,
    };
  }
}

/**
 * @param {"standard" | "plus"} permissionsName
 * @param {number} months
 */
export const getDefaultPlan = (permissionsName, months) => {
  return defaultPlans[`${permissionsName}-${months}`];
};

const durations = {
  0: {
    renewDescription: "Don't Auto-Renew",
  },
  1: {
    renewDescription: "Monthly",
    periodString: "month",
  },
  3: {
    renewDescription: "Tri Monthly",
    periodString: "3 months",
  },
  12: {
    renewDescription: "Yearly",
    periodString: "year",
  },
};

_.each(plans, (plan, id) => {
  plan.id = id;
  _.extend(plan, durations[plan.months]);
});

export const getPlanFromMonths = (months) =>
  getPlanFromField("months", parseInt(months, 10));

/**
 * @returns {{
 *   permissions: "standard" | "plus" | "free";
 *   months: number;
 *   price: number;
 * }}
 */
export const getPlanFromPrice = (price) =>
  getPlanFromField("price", Number(price));

const getPlanFromField = (fieldName, fieldValue) => {
  let result;
  _.every(plans, (plan) => {
    if (plan[fieldName] === fieldValue) {
      result = plan;
      return false;
    }
    return true;
  });
  return result;
};

export const getPermissions = (plan) => permissions[plans[plan].permissions];
